import { useLayoutEffect, useRef } from "react"
import { useMotionValue, useMotionValueEvent } from "framer-motion"
import styled from "@emotion/styled"

import { useConsole } from "contexts/Console"
import { useNavigation } from "contexts/Navigation"

import getMediaQuery from "css/breakpoints"
import { bold, visuallyHidden } from "css/text"

import { PARAM_KEY_CATEGORY, PARAM_KEY_FILTER, PARAM_KEY_RANGE, FILTER_SEPERATOR, setSelected, CN_ACTIVE, CN_DISABLED } from "./utils"
import { useSG } from "./context"
import { a11yOutline } from "css/a11y"
import { useDictionary } from "contexts/Dictionary"

const Main = styled.div`
  position: relative;
  padding-block: 1rem 0.75rem;
  padding-inline: 0;
  margin-inline: 0;
  justify-items: center;
  overflow: auto;
  grid-column: 1 / span 2;
  grid-row: 1;
  justify-self: start;
  scrollbar-width: none;
  display: grid;
  grid-auto-flow: column;
  width: 100%;
  border: none;

  & fieldset {
    border: none;
    margin: 0;
    padding: 0;
    display: grid;
    grid-auto-flow: column;
    justify-self: center;
    width: fit-content;
  }

  .sg-menu-filters & fieldset {
    justify-self: start;
  }
  ${getMediaQuery("m")} {
    .sg-menu-filters & fieldset {
      justify-self: center;
    }
  }

  & fieldset > legend {
    ${visuallyHidden}
  }

  ::-webkit-scrollbar {
    display: none;
  }

  ${getMediaQuery("m")} {
    justify-self: center;
    grid-column: 2 / span 1;
  }

  & fieldset > div {
    display: grid;
    &:first-of-type {
      /* margin-inline-start: 0.5rem; */
      padding-inline-start: 1.25rem;
    }
    &:last-of-type {
      /* margin-inline-end: 0.5rem; */
      padding-inline-end: 2.5rem;
    }
    ${getMediaQuery("m")} {
      &:last-of-type {
        /* margin-inline-end: 0.5rem; */
        padding-inline-end: 1.25rem;
      }
    }
  }

  & input[type="radio"] {
    position: relative;
    appearance: none;
    background-color: #fff;
    width: 0;
    height: 0;
    margin: 0;
    font: inherit;
    color: currentColor;
    display: grid;
    place-content: center;
  }

  &:has(.focus-visible) {
    ${a11yOutline}
  }

  & input[type="radio"].focus-visible + label {
    ${a11yOutline}
  }

  & input[type="radio"]:checked + label {
    color: rgb(var(--green));
  }
`

const Gradi = styled.div`
  grid-column: 1 / span 2;
  grid-row: 1;
  width: 100%;
  height: 100%;
  display: grid;

  ${getMediaQuery("m")} {
    grid-column: 2 / span 1;
  }

  &::after {
    content: "";
    z-index: 0;
    pointer-events: none;
    width: 2.5rem;
    height: 100%;
    background: linear-gradient(270deg, rgba(255 255 255 / 1) 0%, rgba(255 255 255 / 0) 100%);
    inset-inline-end: 0;
    justify-self: end;
    grid-row: 1;
    grid-column: 1;
  }
  &::before {
    content: "";
    z-index: 0;
    pointer-events: none;
    width: 1.25rem;
    height: 100%;
    background: linear-gradient(90deg, rgba(255 255 255 / 1) 0%, rgba(255 255 255 / 0) 100%);
    inset-inline-end: 0;
    justify-self: start;
    grid-row: 1;
    grid-column: 1;
  }
`

const Item = styled.div`
  --text: rgb(var(--light-black));
  position: relative;
  ${bold}
  border: 0;
  background: none;
  color: var(--text);
  text-decoration: none;
  display: flex;
  padding-block: 0.5rem;
  padding-inline: 0.5rem;
  cursor: pointer;
  transition: color 0.3s;
  cursor: default;
  line-height: 1.15;
  & label {
    white-space: nowrap;
    cursor: pointer;
  }

  ${getMediaQuery("m")} {
    padding-inline: 1rem;
  }

  &.${CN_DISABLED} {
    opacity: 0.3;
  }

  &.${CN_ACTIVE} {
    --text: rgb(var(--green));
    &::after {
      opacity: 1;
    }
  }

  @media (hover: hover) and (pointer: fine) {
    &:not(.${CN_DISABLED}):not(.${CN_ACTIVE}):hover {
      cursor: pointer;
      color: rgb(var(--green));
    }
  }
`

function SGCategoriesItem({ label, update, term }) {
  const console = useConsole()

  const { avail_category, category } = useSG()

  const value = term.select || "all"

  const rfmain = useRef(null)

  const active = useMotionValue(isActive(category.get()))
  const disabled = useMotionValue(isDisabled(avail_category.get()))

  function onChange(e) {
    update(value)
  }

  function isActive(v) {
    return +(v === value)
  }

  function isDisabled(v) {
    if (!v) return 0
    const arr = v?.split(FILTER_SEPERATOR)
    const index = arr?.indexOf(value) ?? -1
    const state = index >= 0
    return +!state
  }

  function onActive(v) {
    rfmain.current.classList[v ? "add" : "remove"](CN_ACTIVE)
    rfmain.current?.setAttribute("aria-pressed", !!v)
  }
  useMotionValueEvent(active, "change", onActive)

  function onDisabled(v) {
    rfmain.current.classList[v ? "add" : "remove"](CN_DISABLED)
  }
  useMotionValueEvent(disabled, "change", onDisabled)

  function onSelected(v) {
    active.set(isActive(v))
  }
  useMotionValueEvent(category, "change", onSelected)

  function onAvailable(v) {
    disabled.set(isDisabled(v))
  }
  useMotionValueEvent(avail_category, "change", onAvailable)

  useLayoutEffect(() => {
    rfmain.current.checked = category.get() === value
  }, [])

  const cn = [active.get() ? CN_ACTIVE : null, disabled.get() ? CN_DISABLED : null].filter(Boolean).join(" ")

  return (
    // <Item ref={rfmain} type='button' onClick={onClick} className={cn} aria-pressed={!!active.get()}>
    //   {label}
    // </Item>
    <Item>
      <input type='radio' id={`radio-${value}`} name='category-filters' ref={rfmain} onChange={onChange} className={cn} value={value} />
      <label htmlFor={`radio-${value}`}>{label}</label>
    </Item>
  )
}

export default function SGCategories({ children, item, priority, mayReset, mask }) {
  const console = useConsole()
  const navigation = useNavigation()
  const dictionary = useDictionary()

  const { category } = useSG()

  function update(v) {
    priority.current = "category"
    console.verbose('mayReset(value, "category")', v, mayReset(v, "category"))
    if (category.get() === v) return
    mask(1).then(() => {
      navigation.push(
        setSelected(
          [
            {
              k: PARAM_KEY_CATEGORY,
              v: v,
            },
            {
              k: PARAM_KEY_FILTER,
              v: mayReset(v, "category"),
            },
            {
              k: PARAM_KEY_RANGE,
              v: 0,
            },
          ],
          console
        ),
        { nopageview: true },
        { scroll: false }
      )
    })
  }

  return (
    <>
      <Main className='light-theme' tabIndex='-1'>
        <fieldset>
          <legend>{dictionary.filterResults()}</legend>
          {item?.map(v => (
            <SGCategoriesItem key={`sg-menu-${v.term.select}`} {...v} update={update} />
          ))}
        </fieldset>
        {children}
      </Main>
      <Gradi />
    </>
  )
}
